import { isSafeHostname } from '@/lib/apUtils'
import {
    extractUrlFromHtml,
    fetchAPProfileStats,
    instanceUrlFromHandle,
    lookupAccountOnInstance,
    normalizeAcct,
    parseLinkHeader,
    stripHTML,
} from '@/lib/mastodon'
import {
    assertSafeMastodonUrl,
    canonicalizeInstanceOrigin,
    mastodonFetchJsonWithStatus,
} from '@/lib/mastodonFetch'

// Re-export shared utilities so consumers can import from pixelfed
export {
    extractUrlFromHtml,
    fetchAPProfileStats,
    instanceUrlFromHandle,
    lookupAccountOnInstance,
    normalizeAcct,
    parseLinkHeader,
    stripHTML,
}

const SCOPES = 'read write follow'
const APP_NAME = 'Mackinac Web'
const APP_WEBSITE = 'https://mackinac.social'

interface PixelfedApp {
    client_id: string
    client_secret: string
    vapid_key: string
    instance_url: string
}

export interface PixelfedSession {
    access_token: string
    token_type: string
    scope: string
    created_at: number
    expires_in?: number
    refresh_token?: string
    account: {
        id: string
        username: string
        acct: string
        display_name: string
        url: string
        avatar: string
        note: string
    }
    instance_url: string
}

// PKCE Helpers
function generateCodeVerifier() {
    const array = new Uint8Array(32)
    window.crypto.getRandomValues(array)
    return btoa(String.fromCharCode(...array))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '')
}

async function generateCodeChallenge(verifier: string) {
    const encoder = new TextEncoder()
    const data = encoder.encode(verifier)
    const hash = await window.crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hash))
    return btoa(String.fromCharCode(...hashArray))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '')
}

export async function registerApp(instanceUrl: string): Promise<PixelfedApp> {
    // Clean URL
    const url = instanceUrl.replace(/\/$/, '').replace(/^https?:\/\//, '')
    const fullUrl = `https://${url}`

    // Validate hostname before any network I/O to prevent SSRF
    const hostname = new URL(fullUrl).hostname
    if (!isSafeHostname(hostname)) {
        throw new Error(`Unsafe instance hostname: ${hostname}`)
    }

    // Check if we already have an app for this instance
    const storedApps = JSON.parse(localStorage.getItem('mackinac_pixelfed_apps') ?? '{}')
    if (storedApps[url]) {
        return { ...storedApps[url], instance_url: fullUrl }
    }

    const formData = new FormData()
    formData.append('client_name', APP_NAME)
    formData.append('redirect_uris', window.location.origin + '/pixelfed/callback')
    formData.append('scopes', SCOPES)
    formData.append('website', APP_WEBSITE)

    const res = await fetch(`${fullUrl}/api/v1/apps`, {
        method: 'POST',
        body: formData,
    })

    if (!res.ok) {
        throw new Error(`Failed to register app on ${url}`)
    }

    const data = await res.json()
    const app = {
        client_id: data.client_id,
        client_secret: data.client_secret,
        vapid_key: data.vapid_key,
        instance_url: fullUrl,
    }

    // Store app credentials
    storedApps[url] = app
    localStorage.setItem('mackinac_pixelfed_apps', JSON.stringify(storedApps))

    return app
}

export async function login(instanceUrl: string) {
    try {
        const app = await registerApp(instanceUrl)
        const verifier = await generateCodeVerifier()
        const challenge = await generateCodeChallenge(verifier)

        // Generate random state for CSRF protection
        const state = await generateCodeVerifier() // Reusing verifier generator for random string

        // Store verifier, instance, and state for callback
        sessionStorage.setItem('pixelfed_verifier', verifier)
        sessionStorage.setItem('pixelfed_instance', app.instance_url)
        sessionStorage.setItem('pixelfed_state', state)

        const params = new URLSearchParams({
            client_id: app.client_id,
            redirect_uri: window.location.origin + '/pixelfed/callback',
            response_type: 'code',
            scope: SCOPES,
            code_challenge: challenge,
            code_challenge_method: 'S256',
            state: state,
        })

        window.location.href = `${app.instance_url}/oauth/authorize?${params.toString()}`
    } catch (err) {
        console.error('Pixelfed login failed:', err)
        throw err
    }
}

export async function handleCallback(code: string): Promise<PixelfedSession> {
    const verifier = sessionStorage.getItem('pixelfed_verifier')
    const instanceUrl = sessionStorage.getItem('pixelfed_instance')
    const storedState = sessionStorage.getItem('pixelfed_state')

    // Get state from URL params
    const params = new URLSearchParams(window.location.search)
    const returnedState = params.get('state')

    if (!verifier || !instanceUrl) {
        throw new Error('Missing PKCE data')
    }

    if (!storedState || !returnedState || storedState !== returnedState) {
        throw new Error('Invalid state parameter (potential CSRF attack)')
    }

    // Clean URL to get key for stored app
    const urlKey = instanceUrl.replace(/\/$/, '').replace(/^https?:\/\//, '')
    const storedApps = JSON.parse(localStorage.getItem('mackinac_pixelfed_apps') ?? '{}')
    const app = storedApps[urlKey]

    if (!app) {
        throw new Error('App registration not found')
    }

    const formData = new FormData()
    formData.append('client_id', app.client_id)
    formData.append('client_secret', app.client_secret)
    formData.append('redirect_uri', window.location.origin + '/pixelfed/callback')
    formData.append('grant_type', 'authorization_code')
    formData.append('code', code)
    formData.append('code_verifier', verifier)
    formData.append('scope', SCOPES)

    const res = await fetch(`${instanceUrl}/oauth/token`, {
        method: 'POST',
        body: formData,
    })

    if (!res.ok) {
        throw new Error('Failed to exchange token')
    }

    const tokenData = await res.json()

    // Verify credentials to get user info
    const verifyRes = await fetch(`${instanceUrl}/api/v1/accounts/verify_credentials`, {
        headers: {
            Authorization: `Bearer ${tokenData.access_token}`,
        },
    })

    if (!verifyRes.ok) {
        throw new Error('Failed to verify credentials')
    }

    const account = await verifyRes.json()

    const session: PixelfedSession = {
        ...tokenData,
        refresh_token: tokenData.refresh_token,
        expires_in: tokenData.expires_in,
        account,
        instance_url: instanceUrl,
    }

    // Clear PKCE data
    sessionStorage.removeItem('pixelfed_verifier')
    sessionStorage.removeItem('pixelfed_instance')
    sessionStorage.removeItem('pixelfed_state')

    return session
}

export async function refreshToken(session: PixelfedSession): Promise<PixelfedSession> {
    if (!session.refresh_token) {
        throw new Error('No refresh token available')
    }

    // Clean URL to get key for stored app
    const urlKey = session.instance_url.replace(/\/$/, '').replace(/^https?:\/\//, '')
    const storedApps = JSON.parse(localStorage.getItem('mackinac_pixelfed_apps') ?? '{}')
    const app = storedApps[urlKey]

    if (!app) {
        throw new Error('App registration not found')
    }

    const formData = new FormData()
    formData.append('client_id', app.client_id)
    formData.append('client_secret', app.client_secret)
    formData.append('grant_type', 'refresh_token')
    formData.append('refresh_token', session.refresh_token)
    formData.append('scope', SCOPES)

    const res = await fetch(`${session.instance_url}/oauth/token`, {
        method: 'POST',
        body: formData,
    })

    if (!res.ok) {
        throw new Error('Failed to refresh token')
    }

    const tokenData = await res.json()

    // Update session with new tokens
    const newSession: PixelfedSession = {
        ...session,
        access_token: tokenData.access_token,
        refresh_token: tokenData.refresh_token ?? session.refresh_token, // Use new if provided, else keep old
        expires_in: tokenData.expires_in,
        created_at: Math.floor(Date.now() / 1000), // Update creation time
    }

    setStoredSession(newSession)
    return newSession
}

export async function ensureValidSession(session: PixelfedSession): Promise<PixelfedSession> {
    // Check if token is expired or about to expire (e.g., within 5 minutes)
    // created_at is in seconds (Unix timestamp)
    // expires_in is in seconds
    if (session.expires_in) {
        const now = Math.floor(Date.now() / 1000)
        const expiresAt = session.created_at + session.expires_in
        const buffer = 300 // 5 minutes buffer

        if (now >= expiresAt - buffer) {
            console.log('Pixelfed token expired or expiring soon, refreshing...')
            return refreshToken(session)
        }
    }
    return session
}

export function getStoredSession(): PixelfedSession | null {
    const stored = localStorage.getItem('mackinac_pixelfed_session')
    return stored ? JSON.parse(stored) : null
}

export function setStoredSession(session: PixelfedSession) {
    localStorage.setItem('mackinac_pixelfed_session', JSON.stringify(session))
}

export function clearSession() {
    localStorage.removeItem('mackinac_pixelfed_session')
}

export async function lookupAccount(
    session: PixelfedSession,
    handle: string
): Promise<{ id: string }> {
    const account = await lookupAccountOnInstance(session.instance_url, handle)
    return { id: account.id }
}

export async function getRelationship(
    session: PixelfedSession,
    accountId: string
): Promise<{ id: string; following: boolean; blocking: boolean }> {
    const res = await fetch(
        `${session.instance_url}/api/v1/accounts/relationships?id[]=${encodeURIComponent(accountId)}`,
        {
            headers: {
                Authorization: `Bearer ${session.access_token}`,
            },
        }
    )

    if (!res.ok) {
        throw new Error('Failed to get relationship')
    }

    const data = await res.json()
    if (!Array.isArray(data) || data.length === 0) {
        throw new Error(`No relationship data returned for account ${accountId}`)
    }
    return data[0]
}

export async function followAccount(session: PixelfedSession, accountId: string): Promise<void> {
    const res = await fetch(
        `${session.instance_url}/api/v1/accounts/${encodeURIComponent(accountId)}/follow`,
        {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${session.access_token}`,
            },
        }
    )

    if (!res.ok) {
        throw new Error('Failed to follow account')
    }
}

export async function unfollowAccount(session: PixelfedSession, accountId: string): Promise<void> {
    const res = await fetch(
        `${session.instance_url}/api/v1/accounts/${encodeURIComponent(accountId)}/unfollow`,
        {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${session.access_token}`,
            },
        }
    )

    if (!res.ok) {
        throw new Error('Failed to unfollow account')
    }
}

export async function updateProfile(
    session: PixelfedSession,
    data: {
        display_name?: string
        note?: string
        fields_attributes?: { name: string; value: string }[]
    }
): Promise<PixelfedSession['account']> {
    const formData = new FormData()
    if (data.display_name !== undefined) formData.append('display_name', data.display_name)
    if (data.note !== undefined) formData.append('note', data.note)
    if (data.fields_attributes !== undefined) {
        data.fields_attributes.forEach((field, i) => {
            formData.append(`fields_attributes[${i}][name]`, field.name)
            formData.append(`fields_attributes[${i}][value]`, field.value)
        })
    }

    const res = await fetch(`${session.instance_url}/api/v1/accounts/update_credentials`, {
        method: 'PATCH',
        headers: {
            Authorization: `Bearer ${session.access_token}`,
        },
        body: formData,
    })

    if (!res.ok) {
        throw new Error('Failed to update Pixelfed profile')
    }

    return res.json()
}

// --- Unauthenticated instance API (for public social graph) ---

export interface PixelfedAccount {
    id: string
    username: string
    acct: string
    display_name: string
    avatar: string
    note: string
    url: string
    uri: string
    followers_count: number
    following_count: number
    statuses_count: number
    fields?: Array<{ name: string; value: string; verified_at?: string | null }>
}

export interface PixelfedAccountPage {
    accounts: PixelfedAccount[]
    nextUrl?: string
}

/**
 * Validate a pagination cursor extracted from a Link header against the
 * expected origin. Returns the cursor if safe, or `undefined` (dropping the
 * cursor) if it fails validation — logging a sanitized warning (origins
 * only, never the full URL/query string/headers) rather than throwing, since
 * a hostile Link header should end pagination, not break the current page.
 */
function resolveNextUrl(
    candidateNextUrl: string | undefined,
    expectedOrigin: string,
    operation: string
): string | undefined {
    if (!candidateNextUrl) return undefined

    try {
        assertSafeMastodonUrl(candidateNextUrl, expectedOrigin)
        return candidateNextUrl
    } catch {
        let receivedOrigin = '[unparseable]'
        try {
            receivedOrigin = new URL(candidateNextUrl).origin
        } catch {
            // leave sentinel
        }
        console.warn(
            `[${operation}] dropping unsafe pagination Link cursor: expected origin ${expectedOrigin}, received origin ${receivedOrigin}`
        )
        return undefined
    }
}

/**
 * Fetch followers for an account on a Pixelfed instance (unauthenticated).
 *
 * The first-page URL and any server-supplied pagination cursor are both
 * pinned to `instanceUrl`'s own origin before any network I/O — see
 * `webapp/src/lib/mastodonFetch.ts` for the threat model.
 */
export async function fetchInstanceFollowers(
    instanceUrl: string,
    accountId: string,
    options?: { nextUrl?: string; limit?: number }
): Promise<PixelfedAccountPage> {
    const { origin: expectedOrigin } = canonicalizeInstanceOrigin(instanceUrl)
    const url =
        options?.nextUrl ??
        `${expectedOrigin}/api/v1/accounts/${encodeURIComponent(accountId)}/followers?limit=${options?.limit ?? 40}`

    const result = await mastodonFetchJsonWithStatus<PixelfedAccount[]>(url, {}, { expectedOrigin })
    if (!result.ok) {
        throw new Error(`Failed to fetch followers: ${result.status}`)
    }

    const accounts = result.data
    const nextUrl = resolveNextUrl(
        parseLinkHeader(result.linkHeader),
        expectedOrigin,
        'pixelfed.fetchInstanceFollowers'
    )

    return { accounts, nextUrl }
}

/**
 * Fetch following for an account on a Pixelfed instance (unauthenticated).
 *
 * The first-page URL and any server-supplied pagination cursor are both
 * pinned to `instanceUrl`'s own origin before any network I/O — see
 * `webapp/src/lib/mastodonFetch.ts` for the threat model.
 */
export async function fetchInstanceFollowing(
    instanceUrl: string,
    accountId: string,
    options?: { nextUrl?: string; limit?: number }
): Promise<PixelfedAccountPage> {
    const { origin: expectedOrigin } = canonicalizeInstanceOrigin(instanceUrl)
    const url =
        options?.nextUrl ??
        `${expectedOrigin}/api/v1/accounts/${encodeURIComponent(accountId)}/following?limit=${options?.limit ?? 40}`

    const result = await mastodonFetchJsonWithStatus<PixelfedAccount[]>(url, {}, { expectedOrigin })
    if (!result.ok) {
        throw new Error(`Failed to fetch following: ${result.status}`)
    }

    const accounts = result.data
    const nextUrl = resolveNextUrl(
        parseLinkHeader(result.linkHeader),
        expectedOrigin,
        'pixelfed.fetchInstanceFollowing'
    )

    return { accounts, nextUrl }
}
